import React, { useEffect, useReducer, useState } from "react";
import { useAlertService } from "../../hooks/useAlertService";
import { LastOrderList, TradeType } from "./mmTypes";
import { changeBackground } from "../../hooks/onHover";
import { mainColor } from "../../constants/colors";
interface TradingSettings {
  connection_id: string;
  id: string;
  open_key: string;
  password: string;
  precision_amount: number;
  precision_price: number;
  private_key: string;
}

interface TradingSettingsProps {
  data: TradingSettings;
  pair: string;
  exchange: string;
  return: Function;
  visState: boolean;
}

const changeLiquidSettings = async (
  connectionId: string,
  privateKey: string,
  publicKey: string,
  password: string,
  exchange: string,
  body: any
) => {
  const res = await fetch(
    "https://lukkypro.ru/api/v1/liquid_bots/" +
      connectionId +
      "/edit?trading_sdk_type=" +
      exchange +
      "&public_key=" +
      publicKey +
      "&secret_key=" +
      privateKey +
      "&password=" +
      password,
    {
      method: "POST",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("authToken"),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    throw new Error(`Failed to cancel order. Status: ${res.status}`);
  }
  return await res.json();
};

const createOrderListRequest = async (
  connectionId: string,
  privateKey: string,
  publicKey: string,
  password: string,
  exchange: string,
  body: any
) => {
  const res = await fetch(
    "https://lukkypro.ru/api/v1/liquid_bots/" +
      connectionId +
      "/create_order_list?trading_sdk_type=" +
      exchange +
      "&public_key=" +
      publicKey +
      "&secret_key=" +
      privateKey +
      "&password=" +
      password,
    {
      method: "POST",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("authToken"),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    throw new Error(`Failed to cancel order. Status: ${res.status}`);
  }
  return await res.json();
};

const getLastOrderRequest = async (
  connectionId: string,
  privateKey: string,
  publicKey: string,
  password: string,
  exchange: string
) => {
  const res = await fetch(
    "https://lukkypro.ru/api/v1/liquid_bots/" +
      connectionId +
      "/get_last_order_list?trading_sdk_type=" +
      exchange +
      "&public_key=" +
      publicKey +
      "&secret_key=" +
      privateKey +
      "&password=" +
      password,
    {
      method: "GET",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("authToken"),
        "Content-Type": "application/json",
      },
    }
  );
  if (!res.ok) {
    throw new Error(`Failed to cancel order. Status: ${res.status}`);
  }
  return await res.json();
};

const createOrderList = {
  pair: "string",
  min_price: 0,
  max_price: 0,
  min_step_price: 0,
  max_step_price: 0,
  min_amount: 0,
  max_amount: 0,
  is_buy: "BUY",
};

const InnerLiquidSettings: React.FC<TradingSettingsProps> = (props) => {
  const { showAlert } = useAlertService();
  const [liquidSettings, setLiquidSettings] = useState<TradingSettings>({
    ...props.data,
  });

  const [side, setSide] = useState(TradeType.BUY);
  const [minPrice, setMinPrice] = useState(0);
  const [minAmount, setMinAmount] = useState(0);
  const [maxPrice, setMaxPrice] = useState(0);
  const [maxAmount, setMaxAmount] = useState(0);
  const [minPriceStep, setMinPriceStep] = useState(0);
  const [maxPriceStep, setMaxPriceStep] = useState(0);

  useEffect(() => {
    (async () => {
      const lastOrder = (await getLastOrderRequest(
        props.data.connection_id,
        props.data.private_key,
        props.data.open_key,
        props.data.password,
        props.exchange
      )) as LastOrderList;
      if (!lastOrder) return;

      setSide(lastOrder.is_buy)
      setMinPrice(+lastOrder.min_price)
      setMinAmount(+lastOrder.min_amount)
      setMaxPrice(+lastOrder.max_price)
      setMaxAmount(+lastOrder.max_amount)
      setMinPriceStep(+lastOrder.min_step_price)
      setMaxPriceStep(+lastOrder.max_step_price)
    })();
  }, []);

  const formFields = Object.entries(liquidSettings).map(([key, value]) => ({
    label: key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    value,
    id: key,
  }));

  const formFieldsCreateOrderList = Object.entries(createOrderList).map(
    ([key, value]) => ({
      label: key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      value,
      id: key,
    })
  );

  const onClickHandleSaveChanges = async (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();
    try {
      await changeLiquidSettings(
        props.data.connection_id,
        props.data.private_key,
        props.data.open_key,
        props.data.password,
        props.exchange,
        liquidSettings
      );
      showAlert({ type: "success", message: "Parameters saved successfully!" });
    } catch (error) {
      console.error("Error saving parameters:", error);
      showAlert({
        type: "error",
        message: "Failed to save parameters. Please try again.",
      });
    }
  };

  const onClickHandleCreateOrderList = async (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();
    try {
      const body = {
        pair: props.pair,
        min_price: +minPrice,
        max_price: +maxPrice,
        min_step_price: +minPriceStep,
        max_step_price: +maxPriceStep,
        min_amount: +minAmount,
        max_amount: +maxAmount,
        is_buy: side,
      };

      await createOrderListRequest(
        props.data.connection_id,
        props.data.private_key,
        props.data.open_key,
        props.data.password,
        props.exchange,
        body
      );
      showAlert({ type: "success", message: "Parameters saved successfully!" });
    } catch (error) {
      console.error("Error creating orders list parameters:", error);
      showAlert({
        type: "error",
        message: "Failed to create order list. Please try again.",
      });
    }
  };

  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  const inputHandler = (e: any) => {
    //@ts-ignore
    liquidSettings[e.target.id] = e.target.value;
    setLiquidSettings(liquidSettings);
    forceUpdate();
  };

  const inputCreateListHandler = (e: any) => {
    switch (e.target.id) {
      case "min_price":
        setMinPrice(e.target.value);
        break;
      case "max_price":
        setMaxPrice(e.target.value);
        break;
      case "min_step_price":
        setMinPriceStep(e.target.value);
        break;
      case "max_step_price":
        setMaxPriceStep(e.target.value);
        break;
      case "min_amount":
        setMinAmount(e.target.value);
        break;
      case "max_amount":
        setMaxAmount(e.target.value);
        break;
      default:
        break;
    }
  };

  const getValue = (id: string) => {
    switch (id) {
      case "pair":
        return props.pair;
      case "min_price":
        return minPrice;
      case "max_price":
        return maxPrice;
      case "min_step_price":
        return minPriceStep;
      case "max_step_price":
        return maxPriceStep;
      case "min_amount":
        return minAmount;
      case "max_amount":
        return maxAmount;
      default:
        break;
    }
  };

  return (
    <div
      style={{
        display: props.visState ? "flex" : "none",
        justifyContent: props.visState ? "space-between" : "none",
      }}
    >
      <form style={formStyle}>
        {formFields.map((field, index) => (
          <div key={index} style={inputContainerStyle}>
            <label htmlFor={field.id}>{field.label}</label>
            <input
              onChange={(e) => inputHandler(e)}
              type={typeof field.value === "boolean" ? "checkbox" : "text"}
              id={field.id}
              //@ts-ignore
              value={liquidSettings[field.id] as any}
            />
          </div>
        ))}

        <button
          onMouseOver={(e) => changeBackground(e, mainColor + "1A")}
          onMouseLeave={(e) => changeBackground(e, "transparent")}
          onClick={(e) => onClickHandleSaveChanges(e)}
          className="cursor-pointer"
          style={{
            backgroundColor: "transparent",
            border: "1px solid black",
            borderRadius: "5px",
          }}
        >
          Save changes
        </button>
      </form>
      <form style={formStyle}>
        {formFieldsCreateOrderList.map((field, index) => (
          <div key={index} style={inputContainerStyle}>
            <label htmlFor={field.id}>{field.label}</label>
            {field.id == "is_buy" ? (
              <div className="customCheckBoxHolder">
                <input
                  className="customCheckBoxInput"
                  id="cCB1"
                  type="checkbox"
                  checked={side == "BUY"}
                  onClick={() => setSide(TradeType.BUY)}
                />
                <label className="customCheckBoxWrapper" htmlFor="cCB1">
                  <div className="customCheckBox">
                    <div className="inner">Buy</div>
                  </div>
                </label>

                <input
                  className="customCheckBoxInputSell"
                  id="cCB2"
                  type="checkbox"
                  checked={side == "SELL"}
                  onClick={() => setSide(TradeType.SELL)}
                />
                <label
                  style={{ marginLeft: "auto" }}
                  className="customCheckBoxWrapper"
                  htmlFor="cCB2"
                >
                  <div className="customCheckBox">
                    <div className="inner">Sell</div>
                  </div>
                </label>
              </div>
            ) : (
              <input
                onChange={(e) => inputCreateListHandler(e)}
                type={typeof field.value === "number" ? "number" : "text"}
                id={field.id}
                readOnly={field.id == "pair" ? true : false}
                value={getValue(field.id)}
              />
            )}
          </div>
        ))}

        <button
          className="cursor-pointer"
          style={{
            backgroundColor: "transparent",
            border: "1px solid black",
            borderRadius: "5px",
          }}
          onMouseOver={(e) => changeBackground(e, mainColor + "1A")}
          onMouseLeave={(e) => changeBackground(e, "transparent")}
          onClick={(e) => onClickHandleCreateOrderList(e)}
        >
          Execute
        </button>
      </form>
    </div>
  );
};

const formStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "1rem",
};

const inputContainerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
};

export default InnerLiquidSettings;
